import {
  isSentinel,
  sentinelLabel,
  formatCurrency,
  formatPercent,
  enumLabel,
  DEFINITION_OF_DISABILITY_LABELS,
  INCLUSION_LABELS,
  formatRateGuarantee,
  formatPreExistingConditionMonthsDetail,
} from "./shared";

const NOT_INCLUDED = sentinelLabel("notIncluded");

/** LTD plan design — increments / percentageToDollarAmount ($X, Y%) / notIncluded. */
const formatLtdPlanDesign = (row: Record<string, unknown>): string => {
  const type = row["ltdPlanDesignType"];
  if (isSentinel(type)) return sentinelLabel(type as string);

  if (type === "increments") {
    const dollar = formatCurrency(row["ltdPlanDesignDollarAmount"]);
    return dollar ? `Increments: ${dollar}` : "Increments";
  }
  if (type === "percentageToDollarAmount") {
    const pct = formatPercent(row["ltdPlanDesignPercent"]);
    const dollar = formatCurrency(row["ltdPlanDesignDollarAmount"]);
    if (pct && dollar) return `${pct} to ${dollar}`;
    return pct || dollar || "";
  }
  return "";
};

/** IDI plan design — percentage / percentageToDollarAmount / notIncluded. */
const formatIdiPlanDesign = (row: Record<string, unknown>): string => {
  const type = row["idiPlanDesignType"];
  if (isSentinel(type)) return sentinelLabel(type as string);

  if (type === "percentage")
    return formatPercent(row["idiPlanDesignPercent"]) || "";
  if (type === "percentageToDollarAmount") {
    const pct = formatPercent(row["idiPlanDesignPercent"]);
    const dollar = formatCurrency(row["idiPlanDesignDollarAmount"]);
    if (pct && dollar) return `${pct} to ${dollar}`;
    return pct || dollar || "";
  }
  return "";
};

/** Own Occupation Period — reuses LTD pattern. */
const formatOwnOccupationPeriod = (row: Record<string, unknown>): string => {
  const type = row["ownOccupationPeriodType"];
  if (isSentinel(type)) return sentinelLabel(type as string);

  if (type === "ownOccupationOnly") return "Own Occupation Only";
  const amount = row["ownOccupationPeriodAmount"];
  if (type === "yearsOwnOccupation" && amount != null)
    return `${String(amount)} yrs`;
  if (type === "monthsOwnOccupation" && amount != null)
    return `${String(amount)} mo`;
  return "";
};

const BENEFIT_DURATION_LABELS: Record<string, string> = {
  ssnra: "SSNRA",
  ssnraWithRbd: "SSNRA with RBD",
  rbdWithSsnra: "RBD with SSNRA",
  adea1WithSsnra: "ADEA 1 with SSNRA",
  adea1: "ADEA 1",
  adea2: "ADEA 2",
  adea3: "ADEA 3",
  adea4: "ADEA 4",
  twoYearAdea: "2-Year ADEA",
  threeYearAdea: "3-Year ADEA",
  fiveYearAdea: "5-Year ADEA",
  tenYearAdeaSchedule: "10-Year ADEA Schedule",
  reducingBenefitDuration: "Reducing Benefit Duration",
  ssAdea: "SS/ADEA",
  toAge65: "To Age 65",
  toAge67: "To Age 67",
  laterOfAgeOrSsnra: "Later of Age or SSNRA",
  lesserOfRbdOrNumberOfYears: "Lesser of RBD or Years",
  yearDuration: "Years",
  notIncluded: NOT_INCLUDED,
};

const formatBenefitDuration = (row: Record<string, unknown>): string => {
  const type = row["benefitDurationType"];
  if (isSentinel(type)) return sentinelLabel(type as string);

  const label =
    typeof type === "string" ? (BENEFIT_DURATION_LABELS[type] ?? "") : "";
  const amount = row["benefitDurationAmount"];
  const yearsUnit = Number(amount) === 1 ? "year" : "years";

  // "Years" already means the amount is a year count, so show "2.5 years"
  // rather than the redundant "Years: 2.5 yr".
  if (type === "yearDuration" && amount != null) {
    return `${String(amount)} ${yearsUnit}`;
  }

  if (type === "lesserOfRbdOrNumberOfYears" && amount != null) {
    return `${label}: ${String(amount)} ${yearsUnit}`;
  }

  return label;
};

const formatPreExistingCondition = (row: Record<string, unknown>): string => {
  const type = row["preExistingConditionType"];
  if (isSentinel(type)) return sentinelLabel(type as string);
  if (type === "waived") return "Waived";
  if (type === "none") return "None";
  if (type !== "months") return "";
  const detail = row["preExistingConditionMonthsDetail"];
  return typeof detail === "string" && detail
    ? formatPreExistingConditionMonthsDetail(detail)
    : "";
};

const MENTAL_ILLNESS_LABELS: Record<string, string> = {
  years: "Years",
  months: "Months",
  noLimit: "No Limit",
  payableSameAsAnyOtherDisability: "Payable Same as Any Other Disability",
  included: "Included",
  notIncluded: NOT_INCLUDED,
};

/** Mental Illness Benefit — type + numeric timeframe. */
const formatMentalIllnessBenefit = (row: Record<string, unknown>): string => {
  const type = row["mentalIllnessBenefitType"];
  if (isSentinel(type)) return sentinelLabel(type as string);
  const label =
    typeof type === "string" ? (MENTAL_ILLNESS_LABELS[type] ?? "") : "";
  const timeframe = row["mentalIllnessBenefitTimeframe"];

  // Only the "years"/"months" types carry a numeric timeframe; the rest
  // (included, noLimit, …) are label-only, so don't append a trailing colon.
  const hasTimeframe = type === "years" || type === "months";
  if (hasTimeframe && timeframe != null && label) {
    return `${label}: ${String(timeframe)}`;
  }

  return label;
};

/** Recovery Benefit — dollarAmount / included / notIncluded. */
const formatRecoveryBenefit = (row: Record<string, unknown>): string => {
  const type = row["recoveryBenefitType"];
  if (isSentinel(type)) return sentinelLabel(type as string);

  if (type === "included") return "Included";
  if (type === "dollarAmount")
    return formatCurrency(row["recoveryBenefitAmount"]) || "";
  return "";
};

/** Catastrophic Benefit — percentageAmount / included / notIncluded. */
const formatCatastrophicBenefit = (row: Record<string, unknown>): string => {
  const type = row["catastrophicBenefitType"];
  if (isSentinel(type)) return sentinelLabel(type as string);

  if (type === "included") return "Included";
  if (type === "percentageAmount")
    return formatPercent(row["catastrophicBenefitAmount"]) || "";
  return "";
};

const EMPLOYER_CONTRIBUTION_LABELS: Record<string, string> = {
  oneHundredPercentEmployerPaid: "100% Employer Paid",
  contributory: "Contributory",
  voluntary: "Voluntary",
  bonusUp: "Bonus Up",
  grossUp: "Gross Up",
  notIncluded: NOT_INCLUDED,
};

const idiFormatters: Record<string, (row: Record<string, unknown>) => string> =
  {
    ltdPlanDesign: formatLtdPlanDesign,
    idiPlanDesign: formatIdiPlanDesign,
    gsiBenefitMaximum: (row) => {
      // `formatCurrency("")` is `$0` (Number("") === 0), so an unedited seed
      // would render "$0"; treat empty as blank without touching the shared
      // formatter used by every other LOC.
      const value = row["gsiBenefitMaximum"];
      return value == null || value === "" ? "" : formatCurrency(value) || "";
    },
    eliminationPeriodDays: (row) => {
      const days = row["eliminationPeriodDays"];
      return days != null && days !== "" ? `${String(days)} days` : "";
    },
    ownOccupationPeriod: formatOwnOccupationPeriod,
    definitionOfDisability: enumLabel(
      "definitionOfDisability",
      DEFINITION_OF_DISABILITY_LABELS,
    ),
    benefitDuration: formatBenefitDuration,
    preExistingCondition: formatPreExistingCondition,
    mentalIllnessBenefit: formatMentalIllnessBenefit,
    recoveryBenefit: formatRecoveryBenefit,
    catastrophicBenefit: formatCatastrophicBenefit,
    portability: enumLabel("portability", INCLUSION_LABELS),
    employerContribution: enumLabel(
      "employerContribution",
      EMPLOYER_CONTRIBUTION_LABELS,
    ),
    rateGuarantee: formatRateGuarantee,
  };

export const formatIdiField = (
  fieldId: string,
  row: Record<string, unknown>,
): string | undefined => {
  const formatter = idiFormatters[fieldId];
  return formatter ? formatter(row) : undefined;
};
